import { keyBy, orderBy, sum } from 'lodash';
import { useEffect } from 'react';
import shortid from 'shortid';
import { FS } from '../constants';
import {
  CommentActor,
  CommentRemoveReason,
  CommentStatus,
  IComment,
  ICommentContainer,
  ICommentWithReplies
} from '../types/Comment';
import { Doc, DocReference, generateToDocFn } from '../types/Document';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from './collectionListener';
import {
  LoadingValue,
  removeDoc,
  setDoc,
  store,
  updateDoc,
  updateDocFromPartial,
  useMappedLoadingValue
} from './db';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from './documentListener';
import { now } from './time';

export const toCommentDoc = generateToDocFn<IComment>();
export const commentsCollection = () => store().collection(FS.comments);

const delimiter = '-----';
export const docRefToKey = (r: DocReference) =>
  `${r.collection}${delimiter}${r.id}`;
export const keyToDocRef = (k: string): DocReference => {
  const [collection, id] = k.split(delimiter);
  return { collection, id };
};

// maybe expire by time somehow?
const commentsByEntityStore = createCollectionListenerStore((entityKey) => {
  const r = keyToDocRef(entityKey);
  return new CollectionListener(
    commentsCollection()
      .where('entityRef.collection', '==', r.collection)
      .where('entityRef.id', '==', r.id)
      .where('status', 'in', [CommentStatus.PUBLISHED, CommentStatus.REMOVED]),
    toCommentDoc
  );
});

const commentsByEntityStoreForAdmins = createCollectionListenerStore(
  (entityKey) => {
    const r = keyToDocRef(entityKey);
    return new CollectionListener(
      commentsCollection()
        .where('entityRef.collection', '==', r.collection)
        .where('entityRef.id', '==', r.id),
      toCommentDoc
    );
  }
);

export const useCommentsForEntity = (r: DocReference, isAdmin: boolean) => {
  const store = isAdmin
    ? commentsByEntityStoreForAdmins
    : commentsByEntityStore;
  return useCollectionListener(store(docRefToKey(r)));
};

const toHierarchy = (comments: Doc<IComment>[]): Doc<ICommentWithReplies>[] => {
  const commentsWithReplies: Doc<ICommentWithReplies>[] = orderBy(
    comments,
    [(c) => !c.data.pinned, (c) => c.data.createdAt.toMillis()],
    ['asc', 'asc']
  ).map((c) => {
    return {
      ...c,
      data: {
        ...c.data,
        replies: []
      }
    };
  });
  const byRefKey = keyBy(commentsWithReplies, (c) => docRefToKey(c));
  const rootComments: Doc<ICommentWithReplies>[] = [];
  commentsWithReplies.forEach((c) => {
    if (!c.data.parentRef) {
      rootComments.push(c);
      return;
    }
    const parentRefKey = docRefToKey(c.data.parentRef);
    const parent = byRefKey[parentRefKey];
    if (parent) {
      parent.data.replies.push(c);
    } else {
      console.log('Dangling comment', c);
    }
  });
  return rootComments;
};

export const useCommentHierarchyForEntity = (
  r: DocReference,
  isAdmin: boolean
) => {
  return useMappedLoadingValue(useCommentsForEntity(r, isAdmin), toHierarchy);
};

const newCommentDoc = (
  actor: CommentActor,
  entityRef: DocReference,
  parentRef: DocReference | null,
  body: string
) => {
  const n = now();
  const status =
    actor.type === 'USER' ? CommentStatus.PUBLISHED : CommentStatus.PENDING;
  const published = status === CommentStatus.PUBLISHED;
  const doc: Doc<IComment> = {
    id: shortid(),
    collection: FS.comments,
    data: {
      body,
      createdBy: actor,
      createdAt: n,
      editedBy: actor,
      editedAt: n,
      status,
      publishedBy: published ? actor : null,
      publishedAt: published ? n : null,
      entityRef,
      parentRef,
      pinned: false,
      pinnedBy: null,
      pinnedAt: null,
      removedBy: null,
      removedAt: null,
      removeReason: null,
      replyCount: 0
    }
  };
  return doc;
};

export const createCommentOrReply = async (
  actor: CommentActor,
  entityRef: DocReference,
  parentRef: DocReference | null,
  body: string
) => {
  const doc = newCommentDoc(actor, entityRef, parentRef, body);
  return setDoc(doc);
};

export const editCommentOrReply = async (
  actor: CommentActor,
  id: string,
  body: string
) => {
  // security through permissions - for admins, your own comment, not by guests
  const doc: Doc<Pick<IComment, 'body' | 'editedAt' | 'editedBy'>> = {
    id,
    collection: FS.comments,
    data: {
      body,
      editedBy: actor,
      editedAt: now()
    }
  };
  await updateDocFromPartial(doc);
};

export const pinComment = async (actor: CommentActor, id: string) => {
  // security through permissions - only by admins
  const doc: Doc<Pick<IComment, 'pinned' | 'pinnedBy' | 'pinnedAt'>> = {
    id,
    collection: FS.comments,
    data: {
      pinned: true,
      pinnedBy: actor,
      pinnedAt: now()
    }
  };
  await updateDocFromPartial(doc);
};

export const unpinComment = async (id: string) => {
  // security through permissions - only by admins
  const doc: Doc<Pick<IComment, 'pinned' | 'pinnedBy' | 'pinnedAt'>> = {
    id,
    collection: FS.comments,
    data: {
      pinned: false,
      pinnedBy: null,
      pinnedAt: null
    }
  };
  await updateDocFromPartial(doc);
};

export const publishComment = async (actor: CommentActor, id: string) => {
  // security through permissions - only by admins
  const doc: Doc<Pick<IComment, 'publishedAt' | 'publishedBy' | 'status'>> = {
    id,
    collection: FS.comments,
    data: {
      publishedBy: actor,
      publishedAt: now(),
      status: CommentStatus.PUBLISHED
    }
  };
  await updateDocFromPartial(doc);
};

export const unpublishComment = async (actor: CommentActor, id: string) => {
  // security through permissions - only by admins
  const doc: Doc<Pick<IComment, 'publishedAt' | 'publishedBy' | 'status'>> = {
    id,
    collection: FS.comments,
    data: {
      publishedBy: null,
      publishedAt: null,
      status: CommentStatus.PENDING
    }
  };
  await updateDocFromPartial(doc);
};

export const removeCommentOrReply = async (
  actor: CommentActor,
  doc: Doc<IComment>,
  reason: CommentRemoveReason = CommentRemoveReason.NONE
) => {
  // security through permissions - only by admins or your own?
  // if a comment has replies, blank it out so that the structure still remains
  if (!doc.data.replyCount) {
    await removeDoc(doc);
  } else {
    await updateDoc(doc, () => {
      return {
        body: '',
        removedBy: actor,
        removedAt: now(),
        removeReason: reason,
        status: CommentStatus.REMOVED
      };
    });
  }
};

const toCommentContainerDoc = generateToDocFn<ICommentContainer>();
const toCommentContainerRef = (id: string) =>
  store().collection(FS.commentContainers).doc(id);
const commentContainerStore = createDocumentListenerGetter(
  toCommentContainerRef,
  toCommentContainerDoc
);

export const useCommentContainer = (
  id: string,
  data: {
    href: string;
    title: string;
    metadata?: any;
  }
): LoadingValue<Doc<ICommentContainer>> => {
  const [d, loading, error] = useDocumentListener(commentContainerStore(id));
  const { href, title, metadata } = data;
  useEffect(() => {
    if (d === null) {
      const data: ICommentContainer = {
        __comments: {
          countWithReplies: 0,
          countWthoutReplies: 0,
          href,
          title,
          metadata: metadata || null
        }
      };
      toCommentContainerRef(id).set(data);
    }
  }, [d, id, href, title, metadata]);
  return [d || undefined, loading || d === null, error];
};

export const isSameActor = (a: CommentActor, b: CommentActor) => {
  if (a.type === 'USER' && b.type === 'USER') {
    return a.id === b.id;
  }
  if (a.type === 'GUEST' && b.type === 'GUEST') {
    return a.email === b.email;
  }

  return false;
};

export const getNestedReplyCount = (d: Doc<ICommentWithReplies>): number => {
  return d.data.replies.length + sum(d.data.replies.map(getNestedReplyCount));
};

export const toCommentIdHash = (commentId: string) => `comment-${commentId}`;
